<template>
<div>

  <template v-if="push !== null">
    <table
      class="prop-table mb-3"
    >
      <tr>
        <th>Created</th>
        <td>{{push.ctime | getMomentStr}}</td>
      </tr>
      <tr v-if="isAutoPush">
        <th>TTL</th>
        <td>{{push.ttl | getDurationStr}}</td>
      </tr>
      <tr>
        <th>
          Status
        </th>
        <td>
          <span
            :class="statusClass"
          >
            {{status | getPushStatusName}}
          </span>
          <template v-if="isActive">
            ({{getDonePercentStr}})
          </template>
        </td>
      </tr>
      <tr v-if="hasInfo">
        <th>Info</th>
        <td>{{push.info}}</td>
      </tr>
      <tr v-if="hasLabel">
        <th>Label</th>
        <td>{{push.label}}</td>
      </tr>
      <tr v-if="hasImage">
        <th>Image</th>
        <td><a v-bind:href="push.imageUrl">{{push.imageUrl}}</a></td>
      </tr>
      <tr v-if="hasMaxVersion">
        <th>Max version</th>
        <td>{{maxVersionStr}}</td>
      </tr>
      <tr v-if="hasPlatforms">
        <th>Platforms</th>
        <td>{{platformsStr}}</td>
      </tr>
      <tr v-if="hasPlayersIds">
        <th>Players</th>
        <td>
          <player-links :playerIds="push.playersIds"/>
        </td>
      </tr>

      <!--
        User push (push created by user) has fields stime and srtime.
        Auto push (push created automatically) has not fields stime and srtime.
      -->
      <template v-if="isUserPush">
        <tr>
          <th>
            Expected start
          </th>
          <td>
            {{push.stime | getMomentStr}}
          </td>
        </tr>
        <tr>
          <th>
            Started at
          </th>
          <td>
            {{push.srtime | getMomentStr}}
          </td>
        </tr>
      </template>

      <tr
        v-if="hasStatInfo"
        class="push-stat"
      >
        <th>
          <div>Total</div>
          <div>Sent</div>
          <div>Fails</div>
        </th>
        <td>
          <div class="push-stat-value-box">
            <div>{{push.total | getFormattedNumber}}</div>
            <div>{{push.sent  | getFormattedNumber}}</div>
            <div>{{push.fails | getFormattedNumber}}</div>
          </div>
        </td>
      </tr>
    </table>

    <p>
      {{push.title}}
    </p>
    <p>
      {{push.message}}
    </p>

    <hr>

    <template v-if="isIncoming && push.textVariants !== null">

      <h3 class="mb-3">Text variants</h3>
      <push-text-variants
        :textVariants="push.textVariants"
      />

      <hr>

    </template>

    <template v-if="compactJobs !== null">

      <h3>Jobs report</h3>
      <table class="table table-sm">
        <thead class="thead-dark">
          <tr>
            <th>Title</th>
            <th>Message</th>
            <th>Lang</th>
            <th>Total</th>
            <th>Sent</th>
            <th>Fails</th>
          </tr>
        </thead>
        <tr
          v-for="job in compactJobs"
          :key="job.id"
        >
          <td class="push-info-title">{{job.title}}</td>
          <td class="push-info-message">{{job.message}}</td>
          <td>{{job.language | getLangNameEn}}</td>
          <td class="jobs-report-total">{{job.total | getFormattedNumber}}</td>
          <td class="jobs-report-sent" >{{job.sent  | getFormattedNumber}}</td>
          <td class="jobs-report-fails">{{job.fails | getFormattedNumber}}</td>
        </tr>
      </table>

     <hr>

    </template>

    <b-button v-if="canStart" @click="startPush" variant="primary">Start</b-button>
    <b-button v-if="canPause" @click="pausePush" variant="warning">Pause</b-button>
    <b-button :to="{name: 'push_edit', params: {pushId: push.id}}" variant="primary">Edit</b-button>
    <b-button @click='deletePushWithConfirmation' variant="danger">Delete</b-button>

  </template>

  <p v-if="message !== null">
    {{message}}
  </p>

  <loading-indicator
    v-if="isLoading && push === null"
  />

</div>
</template>

<script>
import auth from '../auth';
import { HTTP } from '../http-common';
import notifier from '../notifier';

import { getPushStatus         } from '../model/push.js';
import { getPushStatusName     } from '../model/push.js';
import { hasPushInfo           } from '../model/push.js';
import { hasPushLabel          } from '../model/push.js';
import { hasPushMaxVersion     } from '../model/push.js';
import { getPushMaxVersionStr  } from '../model/push.js';
import { hasPushPlatforms      } from '../model/push.js';
import { getPushPlatformsStr   } from '../model/push.js';
import { hasPushPlayersIds     } from '../model/push.js';
import { hasPushStatInfo       } from '../model/push.js';
import { isPushIncoming        } from '../model/push.js';
import { isPushActive          } from '../model/push.js';
import { getCompactJobs        } from '../model/push.js';
import { canStartPush          } from '../model/push.js';
import { canPausePush          } from '../model/push.js';
import { getPushStatusClass    } from '../model/push.js';
import { getPushDonePercentStr } from '../model/push.js';
import { isAutoPush            } from '../model/push.js';
import { isUserPush            } from '../model/push.js';
import { hasPushImage          } from '../model/push.js';

/**
 * @typedef { import('../model/push.js').PushStatus } PushStatus
 * @typedef { import('../model/push.js').PushObject } PushObject
 */

import LoadingIndicator from './LoadingIndicator.vue';
import PlayerLinks from './PlayerLinks.vue';
import PushTextVariants from './PushTextVariants.vue';

import { confirmAction } from '../service/confirm';

const LOAD_STATE_INIT          = 1;
const LOAD_STATE_LOADING       = 2;
const LOAD_STATE_LOADED_NORMAL = 3;
const LOAD_STATE_LOADED_ERROR  = 4;

export default {
  name: 'PushInfo',

  /*eslint no-unused-vars: ["error", { "argsIgnorePattern": "^_" }]*/

  components: {
    LoadingIndicator,
    PlayerLinks,
    PushTextVariants,
  },

  props: {
    pushId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      actor: auth.getViewer(),

      loadState: LOAD_STATE_INIT,

      /** @type {?PushObject} */
      push: null,

      updateTimeoutId: null,
    };
  },

  computed: {
    /** @returns {boolean} */
    isLoading() {
      return this.loadState === LOAD_STATE_LOADING;
    },

    /** @returns {boolean} */
    isLoadedNormal() {
      return this.loadState === LOAD_STATE_LOADED_NORMAL;
    },

    /** @returns {?string} */
    message() {
      if(this.isLoadedNormal && this.push === null) {
        return `Push notification with id ${this.pushId} does not exist.`;
      }
      return null;
    },

    /** @returns {boolean} */
    isAutoPush() {
      return isAutoPush(this.push);
    },

    /** @returns {boolean} */
    isUserPush() {
      return isUserPush(this.push);
    },

    /** @returns {PushStatus} */
    status() {
      return getPushStatus(this.push);
    },

    /** @returns {Object} */
    statusClass() {
      return getPushStatusClass(this.push);
    },

    /** @returns {boolean} */
    isIncoming() {
      return isPushIncoming(this.push);
    },

    /** @returns {boolean} */
    isActive() {
      return isPushActive(this.push);
    },

    /** @returns {string} */
    getDonePercentStr() {
      return getPushDonePercentStr(this.push);
    },

    /** @returns {boolean} */
    hasInfo() {
      return hasPushInfo(this.push);
    },

    /** @returns {boolean} */
    hasLabel() {
      return hasPushLabel(this.push);
    },

    /** @returns {boolean} */
    hasImage() {
      return hasPushImage(this.push);
    },

    /** @returns {boolean} */
    hasMaxVersion() {
      return hasPushMaxVersion(this.push);
    },

    /** @returns {string} */
    maxVersionStr() {
      return getPushMaxVersionStr(this.push);
    },

    /** @returns {boolean} */
    hasPlatforms() {
      return hasPushPlatforms(this.push);
    },

    /** @returns {string} */
    platformsStr() {
      return getPushPlatformsStr(this.push);
    },

    /** @returns {boolean} */
    hasPlayersIds() {
      return hasPushPlayersIds(this.push);
    },

    /** @returns {boolean} */
    hasStatInfo() {
      return hasPushStatInfo(this.push);
    },

    /** @returns {?Object[]} */
    compactJobs() {
      return getCompactJobs(this.push.jobs);
    },

    /** @returns {boolean} */
    canStart() {
      return canStartPush(this.push);
    },

    /** @returns {boolean} */
    canPause() {
      return canPausePush(this.push);
    },
  },

  created() {
    notifier.deleteSenderNotifications(this);
    this.loadPush();
  },

  beforeDestroy() {
    this.clearUpdateTimeout();
  },

  methods: {
    loadPush() {
      this.clearUpdateTimeout();

      this.loadState = LOAD_STATE_LOADING;

      const url = `/push/get/${this.pushId}`;
      HTTP.get(url)
      .then(response => {
        this.push = response.data.data;
        this.loadState = LOAD_STATE_LOADED_NORMAL;
        this.createUpdateTimeout();
      })
      .catch(e => {
        this.loadState = LOAD_STATE_LOADED_ERROR;
        notifier.showError(e.response.data, {sender: this});
      });
    },

    startPush() {
      this.clearUpdateTimeout();

      var app = this 
      this.$dialog
      .confirm('Are you sure you want to start this push?')
        .then(function(_) {
          const url = '/push/start/' + app.push.id;
          HTTP.post(url, null)
          .catch(e => {
            notifier.showError(e.response.data, {sender: app});
          })
          .finally(() => {
            app.loadPush();
          });
      })
      .catch(function() {
        //on cancel
      });
    },

    pausePush() {
      this.clearUpdateTimeout();

      const app = this;
      HTTP.post('/push/pause/' + app.push.id, null)
      .finally(() => {
        app.loadPush();
      });
    },


    deletePushWithConfirmation() {
      this.confirmPushDeletion(() => {
        this.deletePush();
      });
    },

    // function onConfirmed()
    confirmPushDeletion(onConfirmed) {
      confirmAction(
        this,
        'Are you sure you want to delete this push notification {env}?',
        'Delete',
        onConfirmed,
      );
    },

    deletePush() {
      this.clearUpdateTimeout();
      notifier.deleteSenderNotifications(this);
      
      const url = '/push/del/' + this.push.id;
      HTTP.post(url)
      .then(() => {
        notifier.showSuccessAfterTransition('Push deleted.');
        this.$router.push({name: 'pushes'});
      })
      .catch(e => {
        notifier.showError(e.response.data, {sender: this});
      });
    },

    createUpdateTimeout() {
      this.clearUpdateTimeout();

      const UPDATE_DELAY_MSEC = 1000;
      this.updateTimeoutId = setTimeout(this.handleUpdateTime, UPDATE_DELAY_MSEC);
    },

    handleUpdateTime() {
      this.clearUpdateTimeout();
      this.loadPush();
    },

    clearUpdateTimeout() {
      if(this.updateTimeoutId !== null) {
        clearTimeout(this.updateTimeoutId);
        this.updateTimeoutId = null;
      }
    },
  },

  filters: {
    getPushStatusName,
  },
}
</script>

<style scoped>
.push-stat > th > div:not(:last-child),
.push-stat-value-box > div:not(:last-child) {
  padding-bottom: .5rem;
}

.push-stat-value-box {
  display: inline-block;
  text-align: right;
}

.push-info-title {
  min-width: 13rem;
}

.push-info-message {
  min-width: 20rem;
}

.jobs-report-total,
.jobs-report-sent,
.jobs-report-fails {
  white-space: nowrap;
  text-align: right;
}
</style>
